"use client"

import { useMemo, useRef, useEffect, useCallback, useState } from "react"
import { createPortal } from "react-dom"
import Image from "next/image"
import { Plus, Loader2 } from "lucide-react"
import { Icon } from "@/shared/components/icons"
import { cn } from "@/shared/lib/utils"
import type { GenerationRow } from "@/shared/lib/supabase/types"

// ─── Types ───────────────────────────────────────────────────────────────────

interface HistorySidebarProps {
  generations: GenerationRow[]
  isLoading: boolean
  activeSessionIds: string[] | null
  collapsed: boolean
  onSelect: (sessionIds: string[]) => void
  onNew: () => void
  onToggleCollapse: () => void
}

/**
 * Client-side session: generations within SESSION_GAP_MINUTES are one "bundle"
 * in the sidebar (there is no separate sessions table).
 */
interface GenerationSession {
  id: string // first generation's id — stable key
  generations: GenerationRow[] // oldest-first within session
  startedAt: string // earliest created_at
}

interface DateGroup {
  label: string
  items: GenerationSession[]
}

// ─── Constants ───────────────────────────────────────────────────────────────

/** Gap (minutes) between consecutive generations that starts a new sidebar session. */
const SESSION_GAP_MINUTES = 30

/** Max sessions in collapsed thumbnail strip. */
const COLLAPSED_SESSION_THUMB_LIMIT = 20

// ─── Helpers ─────────────────────────────────────────────────────────────────

function relativeTime(dateStr: string): string {
  const now = Date.now()
  const d = new Date(dateStr).getTime()
  const diffMs = now - d
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 1) return "Just now"
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })
}

function getThumbUrl(gen: GenerationRow): string | null {
  if (gen.output_url) return gen.output_url
  const params = gen.parameters as Record<string, unknown>
  const urls = params?.output_urls as string[] | undefined
  return urls?.[0] ?? null
}

/**
 * Group DB rows into sidebar sessions from time gaps.
 * Returns sessions newest-first; each session's generations are oldest-first.
 */
function groupIntoSessions(generations: GenerationRow[]): GenerationSession[] {
  if (generations.length === 0) return []

  const sorted = [...generations].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  )

  const gapMs = SESSION_GAP_MINUTES * 60_000
  const sessions: GenerationSession[] = []
  let current: GenerationSession | null = null
  let lastTime = 0

  for (const gen of sorted) {
    const genTime = new Date(gen.created_at).getTime()

    if (!current || genTime - lastTime > gapMs) {
      current = {
        id: gen.id,
        generations: [gen],
        startedAt: gen.created_at,
      }
      sessions.push(current)
    } else {
      current.generations.push(gen)
    }

    lastTime = genTime
  }

  return sessions.reverse()
}

/** Group sessions into date buckets (Today / Yesterday / This Week / Earlier). */
function groupSessionsByDate(sessions: GenerationSession[]): DateGroup[] {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 86_400_000)
  const weekAgo = new Date(today.getTime() - 7 * 86_400_000)

  const groups: Record<string, GenerationSession[]> = {
    Today: [],
    Yesterday: [],
    "This Week": [],
    Earlier: [],
  }

  for (const session of sessions) {
    const d = new Date(session.startedAt)
    if (d >= today) groups["Today"].push(session)
    else if (d >= yesterday) groups["Yesterday"].push(session)
    else if (d >= weekAgo) groups["This Week"].push(session)
    else groups["Earlier"].push(session)
  }

  return Object.entries(groups)
    .filter(([, items]) => items.length > 0)
    .map(([label, items]) => ({ label, items }))
}

/** Total completed output images across all rows in a session. */
function getSessionImageCount(session: GenerationSession): number {
  return session.generations.reduce((sum, gen) => {
    if (gen.status !== "completed") return sum
    const params = gen.parameters as Record<string, unknown>
    const urls = (params?.output_urls as string[]) ?? []
    return sum + (urls.length || (gen.output_url ? 1 : 0))
  }, 0)
}

/** Thumbnail from first completed generation in the session. */
function getSessionThumb(session: GenerationSession): string | null {
  for (const gen of session.generations) {
    if (gen.status === "completed") {
      const url = getThumbUrl(gen)
      if (url) return url
    }
  }
  return null
}

function isSessionFailed(session: GenerationSession): boolean {
  return session.generations.every((g) => g.status === "failed")
}

// ─── Collapsed sidebar ──────────────────────────────────────────────────────

function CollapsedSidebar({
  generations,
  isLoading,
  activeSessionIds,
  onSelect,
  onNew,
  onToggleCollapse,
}: Omit<HistorySidebarProps, "collapsed">) {
  const sessions = useMemo(() => groupIntoSessions(generations), [generations])
  const thumbSessions = useMemo(
    () => sessions.slice(0, COLLAPSED_SESSION_THUMB_LIMIT),
    [sessions]
  )

  return (
    <div className="hidden lg:flex flex-col items-center gap-2 py-4 px-1.5 fixed right-4 top-24 z-50 max-h-[calc(100dvh-8rem)]">
      {/* Expand button */}
      <button
        type="button"
        onClick={onToggleCollapse}
        className="flex items-center justify-center size-10 rounded-lg border border-white/[0.12] bg-background/92 backdrop-blur-sm text-foreground/85 shadow-md shadow-black/30 hover:text-foreground hover:bg-background hover:border-white/[0.2] transition-all"
        aria-label="Expand history"
      >
        <Icon name="arrow-left-2" size={16} className="size-4" />
      </button>

      {/* New generation button */}
      <button
        type="button"
        onClick={onNew}
        className="flex items-center justify-center size-10 rounded-lg border border-white/[0.12] bg-background/92 backdrop-blur-sm text-foreground/85 shadow-md shadow-black/30 hover:text-foreground hover:bg-background hover:border-white/[0.2] transition-all"
        aria-label="New generation"
      >
        <Plus className="size-4" />
      </button>

      {/* Thumbnail strip — one per session */}
      <div className="flex flex-col gap-1.5 max-h-[60vh] overflow-y-auto scrollbar-hide">
        {isLoading && (
          <div className="flex items-center justify-center size-10">
            <Loader2 className="size-4 animate-spin text-muted-foreground/40" />
          </div>
        )}

        {thumbSessions.map((session) => {
          const isSelected =
            activeSessionIds !== null &&
            session.generations.some((g) => activeSessionIds.includes(g.id))
          const thumbUrl = getSessionThumb(session)
          const failed = isSessionFailed(session)

          return (
            <button
              key={session.id}
              type="button"
              onClick={() => onSelect(session.generations.map((g) => g.id))}
              className={cn(
                "relative size-10 shrink-0 rounded-lg overflow-hidden border transition-all",
                isSelected
                  ? "border-accent-blue ring-1 ring-accent-blue/50"
                  : "border-border/20 hover:border-border/50"
              )}
              aria-label={`View session: ${session.generations[0].prompt.slice(0, 30)}`}
            >
              {thumbUrl ? (
                <Image
                  src={thumbUrl}
                  alt=""
                  fill
                  className="object-cover"
                  sizes="40px"
                />
              ) : (
                <div className="flex items-center justify-center size-full bg-card/40">
                  {failed ? (
                    <Icon name="attention-circle" size={14} className="size-3.5 text-red-400/60" />
                  ) : (
                    <Icon name="image" size={14} className="size-3.5 text-muted-foreground/30" />
                  )}
                </div>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ─── Expanded sidebar ────────────────────────────────────────────────────────

function ExpandedSidebar({
  generations,
  isLoading,
  activeSessionIds,
  onSelect,
  onNew,
  onToggleCollapse,
}: Omit<HistorySidebarProps, "collapsed">) {
  const sidebarRef = useRef<HTMLDivElement>(null)

  const dateGroups = useMemo(() => {
    const sessions = groupIntoSessions(generations)
    return groupSessionsByDate(sessions)
  }, [generations])

  const handleClose = useCallback(() => {
    onToggleCollapse()
  }, [onToggleCollapse])

  // Escape key to close
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose()
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [handleClose])

  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])

  const backdrop = mounted
    ? createPortal(
        <button
          type="button"
          onClick={handleClose}
          className="fixed inset-0 z-40 hidden lg:block bg-black/40 transition-opacity duration-200"
          aria-label="Close history"
        />,
        document.body
      )
    : null

  return (
    <>
      {backdrop}
      <div
        ref={sidebarRef}
        className={cn(
          "hidden lg:flex flex-col w-[260px] fixed right-4 top-24 z-50",
          "h-[min(72dvh,calc(100dvh-8rem))]",
          "rounded-l-2xl ml-1 my-1",
          "border border-white/[0.06] border-l-white/[0.08]",
          "shadow-[-8px_0_24px_-8px_rgba(0,0,0,0.4)]",
          "bg-[linear-gradient(145deg,rgba(13,17,23,0.65)_0%,rgba(17,24,32,0.5)_100%)]",
          "backdrop-blur-[24px]"
        )}
      >
        {/* Subtle inner glow for liquid glass effect */}
        <div
          className="pointer-events-none absolute inset-0 rounded-l-2xl overflow-hidden"
          aria-hidden
        >
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%)",
            }}
          />
        </div>

        {/* Header */}
        <div className="relative flex items-center justify-between px-4 py-3.5 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center size-7 rounded-lg bg-white/[0.04]">
              <Icon name="clock" size={14} className="size-3.5 text-muted-foreground" />
            </div>
            <span className="text-sm font-medium text-foreground tracking-tight">
              History
            </span>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="flex items-center justify-center size-8 rounded-lg text-muted-foreground/70 hover:text-foreground hover:bg-white/[0.06] transition-colors"
            aria-label="Collapse history"
          >
            <Icon name="arrow-right-2" size={16} className="size-4" />
          </button>
        </div>

        {/* New generation button */}
        <div className="relative px-3 py-3">
          <button
            type="button"
            onClick={onNew}
            className={cn(
              "flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm",
              "border border-dashed border-white/[0.1]",
              "bg-white/[0.02] hover:bg-white/[0.05]",
              "text-muted-foreground hover:text-foreground",
              "transition-all duration-200"
            )}
          >
            <Plus className="size-4" />
            New Generation
          </button>
        </div>

        {/* Scrollable list */}
        <div className="relative flex-1 min-h-0 overflow-y-auto px-2 pb-4 scrollbar-hide">
          {isLoading && generations.length === 0 && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="size-5 animate-spin text-muted-foreground/40" />
            </div>
          )}

          {!isLoading && generations.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 gap-2">
              <Icon name="image" size={32} className="size-8 text-muted-foreground/20" />
              <p className="text-xs text-muted-foreground/40">No generations yet</p>
            </div>
          )}

          {dateGroups.map((group) => (
            <div key={group.label} className="mb-4">
              <p className="px-2 pb-1.5 pt-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">
                {group.label}
              </p>

              <div className="flex flex-col gap-1">
                {group.items.map((session) => {
                  const isSelected =
                    activeSessionIds !== null &&
                    session.generations.some((g) =>
                      activeSessionIds.includes(g.id)
                    )
                  const thumbUrl = getSessionThumb(session)
                  const imageCount = getSessionImageCount(session)
                  const failed = isSessionFailed(session)
                  const firstPrompt = session.generations[0].prompt

                  return (
                    <button
                      key={session.id}
                      type="button"
                      onClick={() =>
                        onSelect(session.generations.map((g) => g.id))
                      }
                      className={cn(
                        "flex items-start gap-2.5 w-full rounded-xl px-2.5 py-2 text-left transition-all duration-200",
                        isSelected
                          ? "bg-accent-blue/10 border border-accent-blue/25"
                          : "border border-transparent hover:bg-white/[0.04] hover:border-white/[0.04]"
                      )}
                    >
                      <div
                        className={cn(
                          "relative size-11 shrink-0 rounded-lg overflow-hidden border transition-colors",
                          isSelected
                            ? "border-accent-blue/40 ring-1 ring-accent-blue/20"
                            : "border-white/[0.08]"
                        )}
                      >
                        {thumbUrl ? (
                          <Image
                            src={thumbUrl}
                            alt=""
                            fill
                            className="object-cover"
                            sizes="44px"
                          />
                        ) : (
                          <div className="flex items-center justify-center size-full bg-white/[0.04]">
                            {failed ? (
                              <Icon name="attention-circle" size={14} className="size-3.5 text-red-400/60" />
                            ) : (
                              <Icon name="image" size={14} className="size-3.5 text-muted-foreground/30" />
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0 pt-0.5">
                        <p
                          className={cn(
                            "text-xs leading-snug line-clamp-2",
                            isSelected ? "text-foreground" : "text-foreground/85"
                          )}
                        >
                          {firstPrompt}
                        </p>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <span className="text-[9px] text-muted-foreground/60">
                            {imageCount} image{imageCount !== 1 ? "s" : ""}
                          </span>
                          {failed && (
                            <span className="rounded-full bg-red-500/10 px-1.5 py-px text-[9px] font-medium text-red-400">
                              Failed
                            </span>
                          )}
                          <span className="text-[9px] text-muted-foreground/45">
                            {relativeTime(session.startedAt)}
                          </span>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

// ─── Main export ─────────────────────────────────────────────────────────────

export function HistorySidebar({
  generations,
  isLoading,
  activeSessionIds,
  collapsed,
  onSelect,
  onNew,
  onToggleCollapse,
}: HistorySidebarProps) {
  if (collapsed) {
    return (
      <CollapsedSidebar
        generations={generations}
        isLoading={isLoading}
        activeSessionIds={activeSessionIds}
        onSelect={onSelect}
        onNew={onNew}
        onToggleCollapse={onToggleCollapse}
      />
    )
  }

  return (
    <ExpandedSidebar
      generations={generations}
      isLoading={isLoading}
      activeSessionIds={activeSessionIds}
      onSelect={onSelect}
      onNew={onNew}
      onToggleCollapse={onToggleCollapse}
    />
  )
}
